import { hasValue, styles } from "@repo/helpers";
import { ChangeEvent, useMemo } from "react";

import { InputComponent } from "../../basic/input";
import { useSelectorLogic } from "../selectorHook";
import { DdlValue, Props } from "../types";
import style from "./style.module.scss";

/** Native single select. Multi selects use react-select on mobile too, see DropDownList */
export default function DropDownListMobile(props: Props) {
	const { value, setValueFn: setValue } = useSelectorLogic(props);
	const {
		id,
		name,
		valueProp = "value",
		textProp = "label",
		values,
		disabled,
		className,
		loaded = true,
		clearable,
		placeholder,
		tabIndex,
		onKeyDown,
		onFocused,
		onUnfocused,
	} = props;

	const vals = useMemo(
		() => (values ?? []).map((val: any): DdlValue => ({ value: val[valueProp], label: val[textProp] })),
		[values, valueProp, textProp],
	);

	// Options are keyed by index: a native select's values are strings, which would lose the type of numeric ids
	const selectedIndex = useMemo(() => {
		const selected = value as DdlValue;
		return hasValue(selected?.value) ? vals.findIndex((v) => v.value === selected.value) : -1;
	}, [value, vals]);

	const selectValue = loaded && selectedIndex >= 0 ? String(selectedIndex) : "";
	const nothingSelected = selectValue === "";
	const placeholderText = loaded ? (placeholder ?? "Select...") : "Loading...";

	function selectionChanged(event: ChangeEvent<HTMLSelectElement>) {
		const picked = event.target.value;
		setValue(picked === "" ? null : vals[Number(picked)]);
	}

	const fullClassName = styles(
		style.select,
		props.fullLength && style.fullLength,
		nothingSelected && style.emptyDdlOption,
		className,
	);
	return (
		<InputComponent {...props} value={value}>
			<select
				id={id}
				name={name || id}
				className={fullClassName}
				value={selectValue}
				disabled={!loaded || disabled}
				tabIndex={tabIndex}
				onChange={selectionChanged}
				onKeyDown={onKeyDown}
				onFocus={onFocused}
				onBlur={onUnfocused}
			>
				{(clearable || nothingSelected) && <option value="">{placeholderText}</option>}
				{vals.map((v, i) => (
					<option key={i} value={i}>
						{v.label}
					</option>
				))}
			</select>
		</InputComponent>
	);
}
